import React, { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, 
  Switch, FormControlLabel, Typography, Box, Accordion, AccordionSummary, 
  AccordionDetails, Chip, CircularProgress, Alert, InputAdornment, Stack
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon, 
  Palette as PaletteIcon,
  ViewQuilt as ViewQuiltIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';

// Asumimos que estos hooks y tipos están correctamente configurados
import { useGlobalCore } from '../../../core/modules/global';
import { useMetricsCore } from '../../../core/modules/metrics';
import { useCompanyCore } from '../../../core/modules/company';
import { CREATE_METRIC_DIALOG } from '../../../core/constants/dialog';
import { PutMetricTypePayloadRequest } from '../../../core/modules/metrics/interfaces/put-metric-type';
// Un tipo para los datos del formulario. Pydantic en el backend se encarga del camelCase.
// Usamos snake_case en el frontend para consistencia con los modelos del backend.
type MetricFormData = {
  name: string;
  title: string;
  abbreviation: string;
  information1: string;
  showCounter: boolean;
  showGrid: boolean;
  showDistribution: boolean;
  showProducts: boolean;
  productOperation: 'greater' | 'lower' | 'equals';
  productNumber: number;
  backgroundColor1: string;
  textColor1: string;
  textColor2: string;
  backgroundColor3: string;
  textColor3: string;
  backgroundColor4: string;
  textColor4: string;
};

function MetricDialog() {
  const { dialog, closeDialog, showSnackbar } = useGlobalCore();
  const { metricType, setMetricType, putMetricType, getMetricTypes } = useMetricsCore();
  const { company } = useCompanyCore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const { control, formState, handleSubmit, reset, watch } = useForm<MetricFormData>({
    mode: 'onChange'
  });
  const { isValid, errors } = formState;

  // Usamos watch para obtener los valores de los switches y mostrar/ocultar campos dinámicamente
  const showProducts = useWatch({ control, name: 'showProducts' });
  const showCounter = useWatch({ control, name: 'showCounter' });

  const handleClose = () => {
    setApiError(null);
    setMetricType(null); // Limpiar la métrica seleccionada en el estado global/hook
    closeDialog();
  };

  // Cuando se abre el diálogo para editar, `metricType` cambiará.
  // Usamos useEffect para poblar el formulario con sus datos.
  useEffect(() => {
    if (metricType) {
      // `reset` es la forma correcta de poblar el formulario en react-hook-form
      reset({
        name: metricType.name,
        title: metricType.title,
        abbreviation: metricType.abbreviation ? metricType.abbreviation : '',
        information1: metricType.information1 ? metricType.information1 : '',
        showCounter: metricType.showCounter,
        showGrid: metricType.showGrid,
        showDistribution: metricType.showDistribution,
        showProducts: metricType.showProducts,
        productOperation: metricType.productOperation,
        productNumber: metricType.productNumber,
        backgroundColor1: metricType.backgroundColor1 || '#FFFFFF',
        textColor1: metricType.textColor1 || '#000000',
        textColor2: metricType.textColor2 || '#000000',
        backgroundColor3: metricType.backgroundColor3 || '#FFFFFF',
        textColor3: metricType.textColor3 || '#000000',
        backgroundColor4: metricType.backgroundColor4 || '#FFFFFF',
        textColor4: metricType.textColor4 || '#000000',
      });
    }
  }, [metricType, reset]);


  const onSubmit = useCallback(
    async (formData: MetricFormData) => {
      if (!company?.id || !metricType?.id) return;
      
      setIsSubmitting(true);
      setApiError(null);

      // El schema del backend (MetricDisplayUpdate) espera camelCase
      // Pydantic lo manejará si el alias está configurado, pero es más seguro enviarlo así.
      const payload: PutMetricTypePayloadRequest = {
        name: formData.name,
        title: formData.title,
        abbreviation: formData.abbreviation,
        information1: formData.information1,
        showCounter: formData.showCounter,
        showGrid: formData.showGrid,
        showDistribution: formData.showDistribution,
        showProducts: formData.showProducts,
        productOperation: formData.productOperation,
        productNumber: formData.productNumber,
        backgroundColor1: formData.backgroundColor1,
        textColor1: formData.textColor1,
        textColor2: formData.textColor2,
        backgroundColor3: formData.backgroundColor3,
        textColor3: formData.textColor3,
        backgroundColor4: formData.backgroundColor4,
        textColor4: formData.textColor4,
      };

      await putMetricType(company.id, String(metricType.id), payload, 
        () => {
          showSnackbar({ message: 'Configuración actualizada con éxito', options: { variant: 'success' } });
          getMetricTypes(company.id);
          handleClose();
        },
        (error: any) => {
          const message = error.response?.data?.detail || 'Ocurrió un error inesperado.';
          setApiError(message);
        }
      );
      setIsSubmitting(false);
    },
    [company?.id, metricType?.id, putMetricType, getMetricTypes, showSnackbar]
  );
  
  return (
    <Dialog open={dialog === CREATE_METRIC_DIALOG} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h6" component="div" className="font-bold">
          Editar Visualización de Métrica
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Personaliza cómo se muestra la métrica "{metricType?.name}" en los reportes.
        </Typography>
      </DialogTitle>
      
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
            <Stack spacing={2} className="pt-2">
                {apiError && <Alert severity="error" className="my-2">{apiError}</Alert>}

                {/* --- SECCIÓN DE INFORMACIÓN GENERAL --- */}
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography className="font-semibold flex items-center gap-2"><InfoIcon fontSize="small"/> Información General</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2}>
                            <Controller name="name" control={control} render={({ field }) => <TextField {...field} label="Nombre Principal" error={!!errors.name} helperText={errors.name?.message} fullWidth required />} />
                            <Controller name="title" control={control} render={({ field }) => <TextField {...field} label="Título para Escalamiento" error={!!errors.title} helperText={errors.title?.message} fullWidth required />} />
                            <Controller name="abbreviation" control={control} render={({ field }) => <TextField {...field} label="Abreviatura" error={!!errors.abbreviation} helperText={errors.abbreviation?.message} fullWidth required />} />
                            <Controller name="information1" control={control} render={({ field }) => <TextField {...field} label="Texto Informativo" multiline rows={2} fullWidth />} />
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                {/* --- SECCIÓN DE VISIBILIDAD --- */}
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography className="font-semibold flex items-center gap-2"><ViewQuiltIcon fontSize="small"/> Visibilidad de Secciones</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={1}>
                            <Controller name="showCounter" control={control} render={({ field }) => <FormControlLabel control={<Switch {...field} checked={field.value} />} label="Mostrar Contador Principal" />} />
                            <Controller name="showGrid" control={control} render={({ field }) => <FormControlLabel control={<Switch {...field} checked={field.value} />} label="Mostrar Tabla de Detalles (Grid)" />} />
                            <Controller name="showDistribution" control={control} render={({ field }) => <FormControlLabel control={<Switch {...field} checked={field.value} />} label="Mostrar Gráfico de Distribución" />} />
                            <Controller name="showProducts" control={control} render={({ field }) => <FormControlLabel control={<Switch {...field} checked={field.value} />} label="Mostrar Sección de Escalamiento" />} />
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                 {/* --- SECCIÓN DE COLORES --- */}
                 <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography className="font-semibold flex items-center gap-2"><PaletteIcon fontSize="small"/> Paleta de Colores</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2}>
                            <Typography variant="subtitle2">Colores del Contador</Typography>
                            <Stack direction="row" spacing={2}>
                                <Controller name="backgroundColor1" control={control} render={({ field }) => <TextField {...field} label="Fondo" type="color" sx={{minWidth: 100}} InputProps={{startAdornment: <InputAdornment position="start">{field.value}</InputAdornment>}} />} />
                                <Controller name="textColor1" control={control} render={({ field }) => <TextField {...field} label="Texto Principal" type="color" sx={{minWidth: 100}} InputProps={{startAdornment: <InputAdornment position="start">{field.value}</InputAdornment>}} />} />
                                <Controller name="textColor2" control={control} render={({ field }) => <TextField {...field} label="Texto Secundario" type="color" sx={{minWidth: 100}} InputProps={{startAdornment: <InputAdornment position="start">{field.value}</InputAdornment>}} />} />
                            </Stack>
                            {showProducts && (
                                <>
                                <Typography variant="subtitle2" className="pt-2">Colores de Escalamiento</Typography>
                                <Stack direction="row" spacing={2}>
                                    <Controller name="backgroundColor3" control={control} render={({ field }) => <TextField {...field} label="Fondo 1" type="color" sx={{minWidth: 100}} InputProps={{startAdornment: <InputAdornment position="start">{field.value}</InputAdornment>}} />} />
                                    <Controller name="textColor3" control={control} render={({ field }) => <TextField {...field} label="Texto 1" type="color" sx={{minWidth: 100}} InputProps={{startAdornment: <InputAdornment position="start">{field.value}</InputAdornment>}} />} />
                                </Stack>
                                <Stack direction="row" spacing={2}>
                                    <Controller name="backgroundColor4" control={control} render={({ field }) => <TextField {...field} label="Fondo 2" type="color" sx={{minWidth: 100}} InputProps={{startAdornment: <InputAdornment position="start">{field.value}</InputAdornment>}} />} />
                                    <Controller name="textColor4" control={control} render={({ field }) => <TextField {...field} label="Texto 2" type="color" sx={{minWidth: 100}} InputProps={{startAdornment: <InputAdornment position="start">{field.value}</InputAdornment>}} />} />
                                </Stack>
                                </>
                            )}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
                
            </Stack>
        </DialogContent>
        <DialogActions className="p-4">
          <Button onClick={handleClose} disabled={isSubmitting}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={!isValid || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default MetricDialog;